interface PlanFeatures {
    downloads: string;
    mediaTypes: string[];
    videoTemplates: string[];
    tools: string[];
    aiFeatures: string[];
}

export const STARTER_PLAN = 'starter';
export const UNLIMITED_PRO_PLAN = 'unlimited-pro';
export const UNLIMITED_ALL_ACCESS_PLAN = 'unlimited-all-access';
export const BUSINESS_PLAN = 'business';
export const SMALL_BUSINESS_PLAN = 'small-business';
export const ESSENTIALS_PLAN = 'essentials';
export const ALL_ACCESS_PLAN = 'all-access';

// Mapping: Plan Code -> Plan Type (for direct lookup)
const PLAN_CODE_TO_TYPE: Record<string, string> = {
    '4000': STARTER_PLAN,
    '4010': STARTER_PLAN,
    '4200': UNLIMITED_PRO_PLAN,
    '4210': UNLIMITED_PRO_PLAN,
    '4100': UNLIMITED_ALL_ACCESS_PLAN,
    '4110': UNLIMITED_ALL_ACCESS_PLAN,
    // TODO: Check the actual plan code for business/ent plan
    '4500': BUSINESS_PLAN,
    '4510': BUSINESS_PLAN,
    // new plans
    '4015': ESSENTIALS_PLAN,
    '4030': ESSENTIALS_PLAN,
    '4115': ALL_ACCESS_PLAN,
    '4130': ALL_ACCESS_PLAN,
    '4215': SMALL_BUSINESS_PLAN,
};

// Mapping: Plan Type -> Plan Codes (for seeing all codes per plan)
const PLAN_TYPE_TO_CODES = {
    [STARTER_PLAN]: ['4000', '4010'],
    [UNLIMITED_PRO_PLAN]: ['4200', '4210'],
    [UNLIMITED_ALL_ACCESS_PLAN]: ['4100', '4110'],
    [BUSINESS_PLAN]: ['4500', '4510'],
    [ESSENTIALS_PLAN]: ['4015', '4030'],
    [ALL_ACCESS_PLAN]: ['4115', '4130'],
    [SMALL_BUSINESS_PLAN]: ['4215'],
} as const;

const starterPlanFeatures: PlanFeatures = {
    aiFeatures: [],
    downloads: '5 Total Monthly Downloads',
    mediaTypes: ['HD footage', 'Music', 'Photos, vectors, & illustrations'],
    videoTemplates: [],
    tools: ['Maker'],
};

const essentialsPlanFeatures: PlanFeatures = {
    aiFeatures: ['3,000 credits/month', 'Video Editing'],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Video templates', 'Photos, vectors, & illustrations'],
    videoTemplates: [],
    tools: ['Maker'],
};

const allAccessPlanFeatures: PlanFeatures = {
    aiFeatures: ['4,500 credits/month', 'Video Editing', 'Voiceover'],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker'],
};

const smallBusinessPlanFeatures: PlanFeatures = {
    aiFeatures: ['6,000 credits/month', 'Video Editing', 'Voiceover'],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker', 'Admin controls'],
};

const unlimitedProPlanFeatures: PlanFeatures = {
    aiFeatures: [],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker'],
};

const unlimitedAllAccessPlanFeatures: PlanFeatures = {
    aiFeatures: [],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker'],
};

const businessPlanFeatures: PlanFeatures = {
    aiFeatures: [],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker', 'Admin controls'],
};

const plansFeatures = {
    [STARTER_PLAN]: starterPlanFeatures,
    [UNLIMITED_PRO_PLAN]: unlimitedProPlanFeatures,
    [UNLIMITED_ALL_ACCESS_PLAN]: unlimitedAllAccessPlanFeatures,
    [BUSINESS_PLAN]: businessPlanFeatures,
    [ESSENTIALS_PLAN]: essentialsPlanFeatures,
    [ALL_ACCESS_PLAN]: allAccessPlanFeatures,
    [SMALL_BUSINESS_PLAN]: smallBusinessPlanFeatures,
};

export const getPlanFeatures = (planCode: string): PlanFeatures => {
    const planType = PLAN_CODE_TO_TYPE[planCode];
    return plansFeatures[planType as keyof typeof plansFeatures];
};

// Helper function to get plan type from plan code
export const getPlanTypeFromCode = (planCode: string): string => {
    return PLAN_CODE_TO_TYPE[planCode] || STARTER_PLAN;
};

// Helper function to get plan codes for a plan type
export const getPlanCodesForType = (planType: string): readonly string[] => {
    return PLAN_TYPE_TO_CODES[planType as keyof typeof PLAN_TYPE_TO_CODES] || [];
};

// Export the mappings for reference
export { PLAN_CODE_TO_TYPE, PLAN_TYPE_TO_CODES };
